import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TIMESHEET_STATUSES, formatValue } from "../data/data";
import {
  getProjectDetails,
  getProjectTasks,
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  createTask,
  updateTask,
  getTaskAttachments,
  uploadTaskAttachment,
  deleteAttachment
} from "../service/rpc";
import { useUser } from "../context/UserContext";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import "../styles/ContractDetail.css";

const ITEMS_PER_PAGE = 6;

const EMPTY_TS_FORM = { task: "", date: "", hours: "" };
const EMPTY_TASK_FORM = { title: "", due: "", state: "todo", description: "" };

const TASK_STATES = {
  "todo": "To Do",
  "in_progress": "In Progress",
  "done": "Done",
  "cancelled": "Cancelled"
};

const handleAttachmentAction = async (attachmentId, action = 'view', fileName = 'attachment') => {
  try {
    const url = `/web/content/${attachmentId}${action === 'download' ? '?download=true' : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch attachment");

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    if (action === 'download') {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.open(objectUrl, '_blank');
    }

    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch (err) {
    console.error("Action failed:", err);
    alert("Failed to load attachment. Please try again.");
  }
};

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Timesheets");

  const [tsSearch, setTsSearch] = useState("");
  const [tsStatus, setTsStatus] = useState("All");
  const [tsPage, setTsPage] = useState(1);
  const [tsView, setTsView] = useState("list");

  const [taskSearch, setTaskSearch] = useState("");
  const [taskStatus, setTaskStatus] = useState("All");
  const [taskPage, setTaskPage] = useState(1);
  const [taskView, setTaskView] = useState("list");

  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [timesheetsData, setTimesheetsData] = useState([]);
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { uid, loading: userLoading, employeeId, partnerId, isPortal, name: userName } = useUser();

  // Modal State - Timesheet
  const [tsModalOpen, setTsModalOpen] = useState(false);
  const [tsModalMode, setTsModalMode] = useState("create");
  const [tsEditTarget, setTsEditTarget] = useState(null);
  const [tsForm, setTsForm] = useState(EMPTY_TS_FORM);
  const [tsSaving, setTsSaving] = useState(false);

  // Modal State - Task
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalMode, setTaskModalMode] = useState("create");
  const [taskEditTarget, setTaskEditTarget] = useState(null);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);

  const [taskSaving, setTaskSaving] = useState(false);
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadSuccessTaskId, setUploadSuccessTaskId] = useState(null);

  // Modal State - View Attachments
  const [viewAttModalOpen, setViewAttModalOpen] = useState(false);
  const [viewAttTaskId, setViewAttTaskId] = useState(null);
  const [viewAttachments, setViewAttachments] = useState([]);
  const [viewAttachmentsLoading, setViewAttachmentsLoading] = useState(false);

  //mobile view show kanban default

  useEffect(() => {
    if (window.innerWidth < 768) {
      setTsView("timesheet");
      setTaskView("task");
    }
  }, [])


  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        // Fetch everything in parallel
        const tsDomain = [['project_id', '=', parseInt(id)]];
        const taskDomain = [['project_id', '=', parseInt(id)]];

        const [projectData, tasksResp, timesheetsResp] = await Promise.all([
          getProjectDetails(id),
          getProjectTasks(taskDomain),
          getTimesheets(tsDomain)
        ]);

        if (!projectData) {
          setContract(null);
          setError("Project not found or unable to load details.");
          setLoading(false);
          return;
        }

        if (projectData) {
          setContract({
            id: projectData.id,
            name: projectData.name || "Unnamed Project",
            client: projectData.partner_id ? projectData.partner_id[1] : "Unknown",
            budget: projectData.budget || 0,
            status: "Active",
            startDate: projectData.date_start ? projectData.date_start.split(' ')[0] : "N/A",
            endDate: projectData.date_end ? projectData.date_end.split(' ')[0] : "N/A"
          });
        }
        const formatStatus = (val) => {
          return TASK_STATES[val] || "In Progress";
        };

        const formattedTasks = tasksResp.map(tk => ({
          id: tk.id,
          title: tk.name || "Unnamed Task",
          // Strictly use 'state' field as requested, ignore stage_id
          status: tk.state ? formatStatus(tk.state) : "In Progress",
          state_raw: tk.state || "todo",
          assignee: "Self", // xmt.task doesn't have partner_id
          partner_id_raw: null,
          due: tk.date_deadline ? tk.date_deadline.split(' ')[0] : "N/A",
          description: stripHtml(tk.description)
        }));
        setTasksData(formattedTasks);

        const formattedTimesheets = timesheetsResp.timesheets.map(ts => ({
          id: ts.id,
          employee: ts.user_id ? ts.user_id[1] : "Unknown",
          date: ts.create_date ? ts.create_date.split(' ')[0] : "N/A",
          hours: ts.duration || 0,
          task: ts.name || (ts.task_id ? ts.task_id[1] : "General"),
          status: "Done"
        }));
        setTimesheetsData(formattedTimesheets);


      } catch (err) {
        console.error("Failed to load contract details", err);
        setError("Error loading project content: " + (err.message || "Please check your network and try again."));
      } finally {
        setLoading(false);
      }
    };

    if (id && !userLoading && uid) fetchAll();
  }, [id, uid, userLoading]);

  // Handle Timesheet Save
  const handleTsSave = async () => {
    try {
      setTsSaving(true);
      const payload = {
        name: tsForm.task,
        project_id: parseInt(id),
        duration: parseFloat(tsForm.hours) || 0,
        user_id: uid,
      };

      if (!uid) {
        alert("You must be logged in to log time.");
        setTsSaving(false);
        return;
      }

      if (tsModalMode === "create") {
        const newId = await createTimesheet(payload);
        setTimesheetsData(prev => [{
          id: newId,
          employee: userName,
          date: tsForm.date,
          hours: parseFloat(tsForm.hours) || 0,
          task: tsForm.task,
          status: "Done"
        }, ...prev]);
      } else {
        await updateTimesheet(tsEditTarget.id, payload);
        setTimesheetsData(prev => prev.map(ts => ts.id === tsEditTarget.id ? {
          ...ts,
          date: tsForm.date,
          hours: parseFloat(tsForm.hours) || 0,
          task: tsForm.task
        } : ts));
      }
      setTsModalOpen(false);
    } catch (err) {
      console.error("TS Save failed", err);
    } finally {
      setTsSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase();
  };

  // Handle Task Save
  const handleTaskSave = async () => {
    try {
      setTaskSaving(true);
      const payload = {
        project_id: parseInt(id),
        name: taskForm.title,
        user_id: parseInt(uid),
        date_deadline: taskForm.due || false,
        state: taskForm.state || "todo",
        description: taskForm.description || ""
      };

      if (taskModalMode === "create") {
        const newId = await createTask(payload);
        if (pendingAttachments.length > 0) {
          try {
            for (let att of pendingAttachments) {
              await uploadTaskAttachment(newId, att.name, att.base64);
            }
          } catch (e) { console.error("Error uploading pending attachments", e); }
        }
        setPendingAttachments([]);
        setTasksData(prev => [{
          id: newId,
          title: taskForm.title,
          status: TASK_STATES[payload.state] || "In Progress",
          state_raw: payload.state,
          description: taskForm.description || "",
          assignee: userName,
          partner_id_raw: null,
          due: taskForm.due || "N/A"
        }, ...prev]);
      } else {
        await updateTask(taskEditTarget.id, payload);
        setTasksData(prev => prev.map(tk => tk.id === taskEditTarget.id ? {
          ...tk,
          title: taskForm.title,
          status: TASK_STATES[payload.state] || "In Progress",
          state_raw: payload.state,
          description: taskForm.description || "",
          due: taskForm.due || "N/A"
        } : tk));
      }
      setTaskModalOpen(false);
    } catch (err) {
      console.error("Task Save failed", err);
    } finally {
      setTaskSaving(false);
    }
  };

  const filteredTimesheets = useMemo(() => {
    const raw = timesheetsData;
    const q = tsSearch.trim().toLowerCase();

    return raw.filter(ts => {
      const matchStatus = tsStatus === "All" || ts.status === tsStatus;
      const matchSearch = !q ||
        ts.employee.toLowerCase().includes(q) ||
        ts.task.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [id, tsSearch, tsStatus, timesheetsData]);

  useMemo(() => setTsPage(1), [tsSearch, tsStatus]);

  const pagedTimesheets = useMemo(() => {
    const start = (tsPage - 1) * ITEMS_PER_PAGE;
    return filteredTimesheets.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTimesheets, tsPage]);

  const filteredTasks = useMemo(() => {
    const raw = tasksData;
    const q = taskSearch.trim().toLowerCase();

    return raw.filter(tk => {
      const matchStatus = taskStatus === "All" || tk.status === taskStatus;
      const matchSearch = !q ||
        tk.title.toLowerCase().includes(q) ||
        tk.assignee.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [id, taskSearch, taskStatus, tasksData]);

  useMemo(() => setTaskPage(1), [taskSearch, taskStatus]);

  const pagedTasks = useMemo(() => {
    const start = (taskPage - 1) * ITEMS_PER_PAGE;
    return filteredTasks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTasks, taskPage]);

  const dynamicTaskStatuses = useMemo(() => {
    const statuses = new Set(tasksData.map(t => t.status));
    return ["All", ...Array.from(statuses)];
  }, [tasksData]);

  const getBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("done") || s.includes("approved") || s.includes("closed") || s === "done") return "active";
    if (s.includes("progress") || s.includes("pending") || s.includes("doing") || s.includes("in progress")) return "pending";
    if (s.includes("hold") || s.includes("wait") || s.includes("pause")) return "hold";
    if (s.includes("to do") || s.includes("new") || s.includes("open") || s === "todo") return "todo";
    return "cancelled";
  };

  if (loading) {
    return (
      <main className="main">
        <div className="loader-container">
          <div className="loader"></div>
          <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Fetching project details...</p>
        </div>
      </main>
    );
  }

  if (error) return (
    <main className="main">
      <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>Access Restricted</h2>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
        <button className="cd-back" style={{ margin: '0 auto' }} onClick={() => navigate("/contracts")}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Contracts
        </button>
      </div>
    </main>
  );

  if (!contract) {
    return (
      <main className="main">
        <div className="cd-header">
          <button className="cd-back" onClick={() => navigate("/contracts")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Contracts
          </button>
          <h1 className="cd-name">Contract not found</h1>
        </div>
      </main>
    );
  }

  return (
    <main className="main">
      {/* ── MODALS ── */}
      <Modal
        isOpen={tsModalOpen}
        onClose={() => setTsModalOpen(false)}
        title={tsModalMode === "create" ? "Log Time" : "Edit Timesheet"}
        onSave={handleTsSave}
        saveLabel={tsModalMode === "create" ? "Log Time" : "Update"}
        saving={tsSaving}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Date</label>
          <input className="modal-form__input" type="date" value={tsForm.date} onChange={e => setTsForm({ ...tsForm, date: e.target.value })} />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Hours</label>
          <input className="modal-form__input" type="number" step="0.5" value={tsForm.hours} onChange={e => setTsForm({ ...tsForm, hours: e.target.value })} placeholder="0.0" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Description</label>
          <textarea className="modal-form__input" value={tsForm.task} onChange={e => setTsForm({ ...tsForm, task: e.target.value })} placeholder="What did you work on?" rows={6} style={{ resize: 'vertical' }} />
        </div>
      </Modal>

      <Modal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        title={taskModalMode === "create" ? "New Task" : "Edit Task"}
        onSave={handleTaskSave}
        saveLabel={taskModalMode === "create" ? "Create Task" : "Update"}
        saving={taskSaving}
      >

        <div className="modal-form__group">
          <label className="modal-form__label">Task Title</label>
          <input className="modal-form__input" value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Task name..." />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Due Date</label>
          <input className="modal-form__input" type="date" value={taskForm.due} onChange={e => setTaskForm({ ...taskForm, due: e.target.value })} />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Status</label>
          <select className="modal-form__input" value={taskForm.state} onChange={e => setTaskForm({ ...taskForm, state: e.target.value })}>
            {Object.entries(TASK_STATES).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Description</label>
          <textarea className="modal-form__input" value={taskForm.description || ""} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Task description..." rows={7} style={{ resize: 'vertical' }} />
        </div>

        <div className="modal-form__group" style={{ marginTop: '24px', borderTop: '1px solid #edf2f7', paddingTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Attachments
            </span>
            {taskModalMode === "edit" || taskModalMode === "create" ? (
              <label className="btn-upload" style={{ fontSize: '13px', cursor: 'pointer', color: '#6b7fc4', fontWeight: '600' }}>
                {uploadingFile ? "Uploading..." : "+ Upload File"}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (taskModalMode === "create") {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const base64 = event.target.result.split(',')[1];
                        setPendingAttachments(prev => [...prev, { id: 'pending-' + Date.now() + Math.random(), name: file.name, file_size: file.size, base64 }]);
                      };
                      reader.readAsDataURL(file);
                    } else {
                      try {
                        setUploadingFile(true);
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                          const base64 = event.target.result.split(',')[1];
                          await uploadTaskAttachment(taskEditTarget.id, file.name, base64);
                          const updated = await getTaskAttachments(taskEditTarget.id);
                          setTaskAttachments(updated);
                          setUploadingFile(false);
                        };
                        reader.readAsDataURL(file);
                      } catch (err) {
                        console.error("Upload failed", err);
                        setUploadingFile(false);
                      }
                    }
                  }}
                />
              </label>
            ) : null}
          </div>

          <div className="attachment-list">
            {taskModalMode === "create" ? (
              pendingAttachments.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#94a3b8' }}>No attachments yet.</p>
              ) : (
                <div className="attachment-list">
                  {pendingAttachments.map(att => (
                    <div key={att.id} className="attachment-item">
                      <div className="attachment-item__main">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
                        <span className="attachment-item__name attachment-item__name--muted">{att.name}</span>
                      </div>
                      <div className="attachment-item__actions">
                        <span className="attachment-item__meta">{(att.file_size / 1024).toFixed(1)} KB</span>
                        <button
                          onClick={() => setPendingAttachments(prev => prev.filter(p => p.id !== att.id))}
                          className="attachment-item__icon attachment-item__icon--danger"
                          title="Remove Attachment"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              attachmentsLoading ? (
                <p style={{ fontSize: '13px', color: '#94a3b8' }}>Loading attachments...</p>
              ) : taskAttachments.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#94a3b8' }}>No attachments found for this task.</p>
              ) : (
                <div className="attachment-list">
                  {taskAttachments.map(att => (
                    <div key={att.id} className="attachment-item">
                      <div className="attachment-item__main">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
                        <button
                          onClick={() => handleAttachmentAction(att.id, 'view')}
                          className="attachment-item__name attachment-item__name--link"
                          title="View File"
                          onMouseOver={e => e.target.style.textDecoration = 'underline'}
                          onMouseOut={e => e.target.style.textDecoration = 'none'}
                        >
                          {att.name}
                        </button>
                      </div>
                      <div className="attachment-item__actions">
                        <button onClick={() => handleAttachmentAction(att.id, 'download', att.name)} className="attachment-item__icon attachment-item__icon--download" title="Download File">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        </button>
                        <button onClick={() => handleAttachmentAction(att.id, 'view')} className="attachment-item__icon attachment-item__icon--view" title="View in Browser">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        </button>
                        <span className="attachment-item__meta">{(att.file_size / 1024).toFixed(1)} KB</span>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Delete "${att.name}"?`)) {
                              setAttachmentsLoading(true);
                              try {
                                await deleteAttachment(att.id);
                                const updated = await getTaskAttachments(taskEditTarget.id);
                                setTaskAttachments(updated);
                              } catch (err) {
                                console.error("Delete failed", err);
                              }
                              setAttachmentsLoading(false);
                            }
                          }}
                          className="attachment-item__icon attachment-item__icon--delete"
                          title="Delete Attachment"
                          onMouseOver={e => e.currentTarget.style.color = '#e05252'}
                          onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </Modal>

      {/* View Attachments Modal */}
      <Modal isOpen={viewAttModalOpen} onClose={() => setViewAttModalOpen(false)} title="Task Attachments">
        <div className="modal-body" style={{ padding: '' }}>
          {viewAttachmentsLoading ? (
            <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', padding: '20px' }}>Loading attachments...</p>
          ) : viewAttachments.length === 0 ? (
            <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', padding: '20px' }}>No attachments found for this task.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {viewAttachments.map(att => (
                <div className="task-view" key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eef2f6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7fc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
                    <button
                      onClick={() => handleAttachmentAction(att.id, 'view')}
                      style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '14px', color: '#1e293b', fontWeight: '500', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}
                      title="View File"
                      onMouseOver={e => e.target.style.textDecoration = 'underline'}
                      onMouseOut={e => e.target.style.textDecoration = 'none'}
                    >
                      {att.name}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0 }}>
                    <button onClick={() => handleAttachmentAction(att.id, 'download', att.name)} style={{ cursor: 'pointer', border: 'none', color: '#16a34a', display: 'flex', alignItems: 'center', background: '#dcfce7', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', transition: 'all 0.2s' }} title="Download File" onMouseOver={e => e.currentTarget.style.background = '#bbf7d0'} onMouseOut={e => e.currentTarget.style.background = '#dcfce7'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>

                    </button>
                    <button onClick={() => handleAttachmentAction(att.id, 'view')} style={{ cursor: 'pointer', border: 'none', color: '#3b82f6', display: 'flex', alignItems: 'center', background: '#e0f2fe', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', transition: 'all 0.2s' }} title="View in Browser" onMouseOver={e => e.currentTarget.style.background = '#bae6fd'} onMouseOut={e => e.currentTarget.style.background = '#e0f2fe'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>

                    </button>
                    <span style={{ fontSize: '12px', color: '#94a3b8', minWidth: '48px', textAlign: 'right' }}>{(att.file_size / 1024).toFixed(1)} KB</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ── DETAIL HEADER ── */}
      <div className="cd-header">
        <button className="cd-back" onClick={() => navigate("/contracts")}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Contracts
        </button>

        <div className="cd-meta">
          <div className="cd-identity">
            <h1 className="cd-name">{contract.name}</h1>
          </div>

          <div className="cd-stats">
            <div className="cd-stat">
              <span className="cd-stat__label">Budget Value</span>
              <span className="cd-stat__value">{formatValue(contract.budget)}</span>
            </div>
            <div className="cd-divider" />
            <div className="cd-stat">
              <span className="cd-stat__label">Start</span>
              <span className="cd-stat__value">{contract.startDate}</span>
            </div>
            <div className="cd-divider" />
            <div className="cd-stat">
              <span className="cd-stat__label">End</span>
              <span className="cd-stat__value">{contract.endDate}</span>
            </div>

          </div>
        </div>
      </div>



      {/* ── DETAIL TABS ── */}
      <div className="cd-tabs-wrap">
        <div className="cd-tabs">
          {["Timesheets", "Tasks"].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`cd-tab ${activeTab === t ? "cd-tab--active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ── TIMESHEETS ── */}
      {activeTab === "Timesheets" && (
        <>

          <div className="task-tab-header">
            <button className="btn-create" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              if (activeTab === "Timesheets") {
                setTsModalMode("create");
                setTsForm({ ...EMPTY_TS_FORM, date: today });
                setTsModalOpen(true);
              } else {
                setTaskModalMode("create");
                setTaskForm({ ...EMPTY_TASK_FORM, due: today });
                setPendingAttachments([]);
                setTaskModalOpen(true);
              }
            }}>
              + {activeTab === "Timesheets" ? "New" : "New Task"}
            </button>
            <SearchBar
              search={tsSearch}
              setSearch={setTsSearch}
              placeholder="Search by employee or task…"
              style={{ flex: 1, margin: 0 }}
            />

            <div className="view-toggle">
              <button
                className={`view-toggle__btn ${tsView === 'list' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTsView('list')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                List
              </button>
              <button
                className={`view-toggle__btn ${tsView === 'grid' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTsView('grid')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
                Kanban
              </button>
            </div>
          </div>

          <div className="table-card">
            {tsView === "list" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th className="table__th" style={{ width: '180px' }}>Date</th>
                    <th className="table__th" style={{ width: '120px' }}>Hours</th>
                    <th className="table__th">Description</th>
                    <th className="table__th" style={{ width: '80px', textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTimesheets.length === 0 ? (
                    <tr><td className="table__empty" colSpan={5}>No timesheets match your filter.</td></tr>
                  ) : pagedTimesheets.map((ts) => (
                    <tr key={ts.id} className="table__row">
                      <td className="table__td"><span className="table__client">{ts.date}</span></td>
                      <td className="table__td"><span className="table__value">{ts.hours}h</span></td>
                      <td className="table__td"><span className="table__client">{ts.task}</span></td>
                      <td className="table__td" style={{ textAlign: 'right' }}>
                        <button className="btn-row-edit" onClick={() => {
                          setTsModalMode("edit");
                          setTsEditTarget(ts);
                          setTsForm({ task: ts.task, date: ts.date, hours: String(ts.hours) });
                          setTsModalOpen(true);
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="task-grid">
                {pagedTimesheets.length === 0 ? (
                  <div className="table__empty" style={{ padding: '40px' }}>No timesheets match your filter.</div>
                ) : pagedTimesheets.map((ts) => (
                  <div key={ts.id} className="kanban-card">
                    <div style={{ display: 'flex', justifyContent: 'right ', alignItems: 'center', gap: '8px' }} className="kanban-card__top">
                      {/* <span className={`badge badge--${getBadgeClass(ts.status)}`}>{ts.status}</span> */}

                      <button className="btn-row-edit" style={{ width: '26px', height: '26px' }} onClick={() => {
                        setTsModalMode("edit");
                        setTsEditTarget(ts);
                        setTsForm({ task: ts.task, date: ts.date, hours: String(ts.hours) });
                        setTsModalOpen(true);
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                    <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.5', marginBottom: '12px' }}>{ts.task}</div>
                    <div className="kanban-card__bottom">
                      <div style={{ fontWeight: '700', color: '#1a2332' }}>{ts.date}</div>
                      <span className="kanban-card__id">{ts.hours}h</span>

                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={tsPage}
              totalItems={filteredTimesheets.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setTsPage}
            />
          </div>
        </>
      )}

      {/* ── TASKS ── */}
      {activeTab === "Tasks" && (
        <>
          <div className="task-tab-header">
            <button className="btn-create" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              if (activeTab === "Timesheets") {
                setTsModalMode("create");
                setTsForm({ ...EMPTY_TS_FORM, date: today });
                setTsModalOpen(true);
              } else {
                setTaskModalMode("create");
                setTaskForm({ ...EMPTY_TASK_FORM, due: today });
                setPendingAttachments([]);
                setTaskModalOpen(true);
              }
            }}>
              + {activeTab === "Timesheets" ? "Log Time" : "New"}
            </button>

            <SearchBar
              search={taskSearch}
              setSearch={setTaskSearch}
              placeholder="Search by title or assignee…"
              style={{ flex: 1, margin: 0 }}
            />


            <div className="view-toggle">
              <button
                className={`view-toggle__btn ${taskView === 'list' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTaskView('list')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                List
              </button>
              <button
                className={`view-toggle__btn ${taskView === 'grid' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTaskView('grid')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
                Kanban
              </button>
            </div>
          </div>

          <div className="table-card">
            <div className="status-pills">
              {dynamicTaskStatuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setTaskStatus(s)}
                  className={`status-pills__pill status-pills__pill--${getBadgeClass(s)} ${taskStatus === s ? "status-pills__pill--selected" : ""}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {taskView === "list" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th className="table__th">Date</th>
                    <th className="table__th">Task</th>
                    <th className="table__th">Status</th>
                    <th className="table__th" style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTasks.length === 0 ? (
                    <tr><td className="table__empty" colSpan={5}>No tasks match your filter.</td></tr>
                  ) : pagedTasks.map((tk) => (
                    <tr key={tk.id} className="table__row">
                      <td className="table__td"><span className="table__client">{tk.due}</span></td>
                      <td className="table__td"><span className="table__name">{tk.title}</span></td>
                      <td className="table__td">
                        <span className={`badge badge--${getBadgeClass(tk.status)}`}>
                          {tk.status}
                        </span>
                      </td>
                      <td className="table__td" style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          {/* Eye Icon (View Attachments) */}
                          <button
                            className="btn-row-view-att"
                            style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }}
                            title="View Attachments"
                            onMouseOver={e => { e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                            onMouseOut={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e8edf3'; }}
                            onClick={() => {
                              setViewAttTaskId(tk.id);
                              setViewAttModalOpen(true);
                              setViewAttachmentsLoading(true);
                              getTaskAttachments(tk.id).then(atts => {
                                setViewAttachments(atts);
                                setViewAttachmentsLoading(false);
                              }).catch(() => setViewAttachmentsLoading(false));
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>

                          {/* Paperclip Icon (Attach File) */}
                          <label className="btn-row-upload" style={{ cursor: 'pointer', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: uploadSuccessTaskId === tk.id ? '#e8f5f0' : '#f8fafc', border: `1.5px solid ${uploadSuccessTaskId === tk.id ? '#10b981' : '#e8edf3'}`, borderRadius: '6px', color: uploadSuccessTaskId === tk.id ? '#10b981' : '#6b7fc4', transition: 'all 0.3s' }} title="Attach File">
                            {uploadSuccessTaskId === tk.id ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                            )}
                            <input
                              type="file"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                try {
                                  const reader = new FileReader();
                                  reader.onload = async (event) => {
                                    const base64 = event.target.result.split(',')[1];
                                    await uploadTaskAttachment(tk.id, file.name, base64);
                                    setUploadSuccessTaskId(tk.id);
                                    setTimeout(() => setUploadSuccessTaskId(null), 3000);
                                  };
                                  reader.readAsDataURL(file);
                                } catch (err) {
                                  console.error("Upload failed", err);
                                }
                              }}
                            />
                          </label>

                          {/* Edit Icon */}
                          <button className="btn-row-edit" style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }} onMouseOver={e => { e.currentTarget.style.color = '#1e293b'; e.currentTarget.style.borderColor = '#cbd5e1'; }} onMouseOut={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e8edf3'; }} onClick={() => {
                            setTaskModalMode("edit");
                            setTaskEditTarget(tk);
                            setTaskForm({ title: tk.title, due: tk.due === "N/A" ? "" : tk.due, partner_id: tk.partner_id_raw ? tk.partner_id_raw[0] : "", state: tk.state_raw, description: tk.description || "" });
                            setTaskModalOpen(true);
                            setAttachmentsLoading(true);
                            getTaskAttachments(tk.id).then(att => {
                              setTaskAttachments(att);
                              setAttachmentsLoading(false);
                            });
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="task-grid">
                {pagedTasks.length === 0 ? (
                  <div className="table__empty" style={{ padding: '40px' }}>No tasks match your filter.</div>
                ) : pagedTasks.map((tk) => (
                  <div key={tk.id} className="kanban-card">
                    <div className="kanban-card__top">
                      <span className={`badge badge--${getBadgeClass(tk.status)}`}>{tk.status}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label className="btn-row-upload" style={{ cursor: 'pointer', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: uploadSuccessTaskId === tk.id ? '#2eaa6e' : '#6b7fc4', borderRadius: '6px', background: uploadSuccessTaskId === tk.id ? '#e8f5f0' : '#f8fafc', border: '1.5px solid #e8edf3', transition: 'all 0.2s' }} title="Attach File">
                          {uploadSuccessTaskId === tk.id ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                          )}
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              try {
                                const reader = new FileReader();
                                reader.onload = async (event) => {
                                  const base64 = event.target.result.split(',')[1];
                                  await uploadTaskAttachment(tk.id, file.name, base64);
                                  setUploadSuccessTaskId(tk.id);
                                  setTimeout(() => setUploadSuccessTaskId(null), 3000);
                                };
                                reader.readAsDataURL(file);
                              } catch (err) {
                                console.error("Upload failed", err);
                              }
                            }}
                          />
                        </label>
                        <button
                          className="btn-row-view-att"
                          style={{ width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }}
                          title="View Attachments"
                          onMouseOver={e => { e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          onMouseOut={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e8edf3'; }}
                          onClick={() => {
                            setViewAttTaskId(tk.id);
                            setViewAttModalOpen(true);
                            setViewAttachmentsLoading(true);
                            getTaskAttachments(tk.id).then(atts => {
                              setViewAttachments(atts);
                              setViewAttachmentsLoading(false);
                            }).catch(() => setViewAttachmentsLoading(false));
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        <button className="btn-row-edit" style={{ width: '26px', height: '26px' }} onClick={() => {
                          setTaskModalMode("edit");
                          setTaskEditTarget(tk);
                          setTaskForm({
                            title: tk.title,
                            due: tk.due === "N/A" ? "" : tk.due,
                            partner_id: tk.partner_id_raw ? tk.partner_id_raw[0] : "",
                            state: tk.state_raw
                          });
                          setTaskModalOpen(true);

                          // Load attachments
                          setAttachmentsLoading(true);
                          getTaskAttachments(tk.id).then(att => {
                            setTaskAttachments(att);
                            setAttachmentsLoading(false);
                          });
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <h3 className="kanban-card__title">{tk.title}</h3>
                    <div className="kanban-card__bottom">
                      <div className="kanban-card__due">{tk.due}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={taskPage}
              totalItems={filteredTasks.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setTaskPage}
            />
          </div>
        </>
      )}
    </main>
  );
}
